import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ServiceType } from '../types'

interface ServiceStatusUpdate {
  serviceId: ServiceType
  status: 'active' | 'inactive' | 'error'
  timestamp: string
}

interface StatusNotificationProps {
  notifications: ServiceStatusUpdate[]
  onDismiss: (index: number) => void
  onClearAll: () => void
}

export const StatusNotification: React.FC<StatusNotificationProps> = ({
  notifications,
  onDismiss,
  onClearAll
}) => {
  const [visibleNotifications, setVisibleNotifications] = useState<ServiceStatusUpdate[]>([])

  useEffect(() => {
    setVisibleNotifications(notifications)
  }, [notifications])

  const getStatusIcon = (status: 'active' | 'inactive' | 'error') => {
    switch (status) {
      case 'active':
        return '✅'
      case 'inactive':
        return '⚠️'
      case 'error':
        return '❌'
      default:
        return '📡'
    }
  }

  const getStatusColor = (status: 'active' | 'inactive' | 'error') => {
    switch (status) {
      case 'active':
        return 'from-green-500 to-emerald-500'
      case 'inactive':
        return 'from-yellow-500 to-orange-500'
      case 'error':
        return 'from-red-500 to-red-600'
      default:
        return 'from-gray-500 to-gray-600'
    }
  }

  const getStatusMessage = (notification: ServiceStatusUpdate) => {
    const serviceName = notification.serviceId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
    
    switch (notification.status) {
      case 'active':
        return `${serviceName} is now online`
      case 'inactive':
        return `${serviceName} is under maintenance`
      case 'error':
        return `${serviceName} is currently offline`
      default:
        return `${serviceName} status updated`
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)

    if (diffSeconds < 60) {
      return 'Just now'
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`
    } else {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }

  if (visibleNotifications.length === 0) {
    return null
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {/* Clear All Button */}
      {visibleNotifications.length > 1 && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={onClearAll}
          className="w-full bg-gray-800/90 backdrop-blur-sm text-gray-300 text-sm py-2 px-3 rounded-lg border border-gray-600/50 hover:bg-gray-700/90 transition-colors"
        >
          Clear All Notifications
        </motion.button>
      )}

      {/* Notifications */}
      <AnimatePresence>
        {visibleNotifications.map((notification, index) => (
          <motion.div
            key={`${notification.serviceId}-${notification.timestamp}`}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="bg-gray-900/95 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 shadow-lg"
          >
            <div className="flex items-start space-x-3">
              {/* Status Icon */}
              <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${getStatusColor(notification.status)} flex items-center justify-center flex-shrink-0`}>
                <span className="text-white text-sm">
                  {getStatusIcon(notification.status)}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">
                  Service Status Update
                </p>
                <p className="text-gray-300 text-sm mt-1">
                  {getStatusMessage(notification)}
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  {formatTimestamp(notification.timestamp)}
                </p>
              </div>

              {/* Dismiss Button */}
              <button
                onClick={() => onDismiss(index)}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0 p-1"
                aria-label="Dismiss notification"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Auto-dismiss progress bar */}
            <motion.div
              className={`mt-3 h-1 bg-gradient-to-r ${getStatusColor(notification.status)} rounded-full`}
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 5, ease: 'linear' }}
              onAnimationComplete={() => onDismiss(index)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}